"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useState, useRef, useEffect } from "react";

type ExpandableTextProps = {
  head?: string | null;
  textStyle?: string | null;
  text: string | null;
  collapsedHeight?: number;
};

export default function ExpandableText({ text, head, textStyle, collapsedHeight = 120 }: ExpandableTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;

    const updateHeight = () => {
      if (contentRef.current) {
        const fullHeight = contentRef.current.scrollHeight;
        setNeedsTruncation(fullHeight > collapsedHeight);
      }
    };

    const observer = new ResizeObserver(updateHeight);
    observer.observe(contentRef.current);

    // Run once initially
    updateHeight();

    return () => observer.disconnect();
  }, [text, collapsedHeight]);

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        <motion.div key={isExpanded ? "expanded" : "collapsed"} initial={{ height: needsTruncation ? collapsedHeight : "auto" }} animate={{ height: !needsTruncation ? "auto" : isExpanded ? "auto" : collapsedHeight }} exit={{ height: collapsedHeight }} transition={{ duration: 0.35, ease: "easeInOut" }} className="overflow-hidden">
          <div ref={contentRef}>
            <p className={`whitespace-pre-line ${textStyle}`}>
              <strong>{head}</strong> {text}{" "}
              {needsTruncation && (
                <button onClick={() => setIsExpanded((prev) => !prev)} className={`text-primaryBlue transition duration-300 font-medium hover:font-bold ${isExpanded ? "ml-2 inline" : "absolute -bottom-0.5 right-0 bg-white px-5 whitespace-nowrap"}`}>
                  {isExpanded ? "Show Less" : "Read More"}
                </button>
              )}
            </p>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
